import {captureException} from '@sentry/browser';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {z} from 'zod';
import {makeZodI18nMap} from 'zod-i18n-map';

import {initReactI18next} from 'react-i18next';

import {FALLBACK_LANGUAGE} from './fallbackLanguage';
import cs_CZ from './locales/cs-CZ.json';
import de_DE from './locales/de-DE.json';
import en_GB from './locales/en-GB.json';
import pl_PL from './locales/pl-PL.json';
import sk_SK from './locales/sk-SK.json';

// Map a raw `window.location.pathname` to a stable route template, replacing
// dynamic VIN/ID segments with `:vin` so Sentry events don't leak user data.
// Defined at module scope so it stays out of React's render path.
const scrubPathname = (pathname: string): string => {
  // `/report/<vin>` → `/report/:vin`. Anything else is a static route already.
  const REPORT_VIN_ROUTE_PREFIX = '/report/';
  if (pathname.startsWith(REPORT_VIN_ROUTE_PREFIX)) {
    return `${REPORT_VIN_ROUTE_PREFIX}:vin`;
  }
  return pathname;
};

const i18n = i18next.createInstance();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'mira_locale',
      caches: ['localStorage'],
    },
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: ['en', 'cs', 'sk', 'pl', 'de'],
    nonExplicitSupportedLngs: true,
    defaultNS: 'translation',
    ns: ['translation'],
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key) => {
      // Drop query params AND scrub dynamic path segments before sending to
      // Sentry — Mira routes embed VIN / vehicle identifiers in the path
      // (e.g. `/report/<vin>`) which are PII-adjacent. We log a route
      // *template* instead of the raw URL so the breadcrumb stays useful for
      // debugging without leaking user data.
      const route = scrubPathname(window.location.pathname);
      captureException(new Error(`Missing translation key: ${key} on route ${route}`));
    },
    resources: {
      cs: {translation: cs_CZ},
      de: {translation: de_DE},
      en: {translation: en_GB},
      pl: {translation: pl_PL},
      sk: {translation: sk_SK},
    },
    partialBundledLanguages: true,
    // `escapeValue: false` is safe in React because JSX text nodes are
    // automatically escaped. INVARIANT: translation output must NEVER be
    // rendered via `dangerouslySetInnerHTML` or `innerHTML`.
    interpolation: {
      escapeValue: false,
      formatSeparator: ',',
      prefix: '{',
      suffix: '}',
    },
    react: {
      useSuspense: true,
    },
    pluralSeparator: '.',
  });

z.setErrorMap(makeZodI18nMap({t: i18n.t, ns: ['translation']}));

/**
 * Keep `<html lang>` in sync with the active i18n language. Module-scope
 * listener keeps this out of React's render path so we avoid a `useEffect`
 * (which the repo bans by default — see `.agents/skills/avoiding-use-effect`).
 */
const syncHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};

i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.language);

export {i18n};
